import Store from 'repatch';

import State from '../core/State';
import UserPersonalInformation, { Sex } from '../entity/UserPersonalInformation';
import { setCountries } from '../reducer/countries';
import { finishSpinner, startSpinner } from '../reducer/fullScreenSpinner';
import { setLocationCode } from '../reducer/locationCode';
import { setUserPersonalInformation } from '../reducer/userPersonal';
import ApiErrorHandler from '../service/ApiErrorHandler';
import MessageDispatcher from '../service/MessageDispatcher';
import UserPersonalWebApi from '../service/persistent/UserPersonalWebApi';

class UserPersonalUsecase {
  private store: Store<State>;
  private apiErrorHandler: ApiErrorHandler;
  private userPersonalWebApi: UserPersonalWebApi;
  private messageDispatcher: MessageDispatcher;

  constructor({
    store,
    apiErrorHandler,
    userPersonalWebApi,
    messageDispatcher,
  }: {
    store: Store<State>;
    apiErrorHandler: ApiErrorHandler;
    userPersonalWebApi: UserPersonalWebApi;
    messageDispatcher: MessageDispatcher;
  }) {
    this.store = store;
    this.apiErrorHandler = apiErrorHandler;
    this.userPersonalWebApi = userPersonalWebApi;
    this.messageDispatcher = messageDispatcher;
  }

  public getCountries = async (): Promise<void> => {
    try {
      const countries = await this.userPersonalWebApi.getCountries();
      this.store.dispatch(setCountries(countries));
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    }
  };

  public getLocation = async (): Promise<void> => {
    try {
      const locationCode = await this.userPersonalWebApi.getLocation();
      if (locationCode) {
        this.store.dispatch(setLocationCode(locationCode));
      }
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    }
  };

  public getUserPersonalInformation = async (id: string): Promise<void> => {
    try {
      const info = await this.userPersonalWebApi.getUserPersonalInformation(id);
      this.store.dispatch(setUserPersonalInformation(info));
    } catch (_err) {
      const err: any = _err;
      if (err.response.status == 404) {
        this.store.dispatch(
          setUserPersonalInformation(
            new UserPersonalInformation({
              lastName: '',
              firstName: '',
              nationality: null,
              sex: null,
              birthdate: null,
              freezed: false,
            }),
          ),
        );
      } else {
        this.apiErrorHandler.handle(err);
        return Promise.reject();
      }
    }
  };

  public updatePersonalInformation = async ({
    lastName,
    firstName,
    nationality,
    birthdate,
    sex,
  }: {
    lastName: string;
    firstName: string;
    nationality: string;
    birthdate: string;
    sex: Sex;
  }): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      await this.userPersonalWebApi.updatePersonalInformation({ lastName, firstName, nationality, birthdate, sex });
      this.messageDispatcher.notifyUnimportant('user_setting.personal.settings_updated');
      this.store.dispatch(
        setUserPersonalInformation(
          new UserPersonalInformation({ lastName, firstName, nationality, birthdate, sex, freezed: true }),
        ),
      );
      return Promise.resolve();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };
}

export default UserPersonalUsecase;
